import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { 
  clientLogos,
  services,
  certifications,
  caseStudies,
  testimonials,
  pricingTiers
} from './constants'; // We'll create this file next

export default function MainPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Success Modal Component
  const SuccessModal = () => (
    <motion.div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg 
              className="h-6 w-6 text-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Message Sent Successfully!</h3>
          <p className="text-gray-600 mb-6">
            The HunterFortress team will contact you soon about your {selectedTier?.name} package. 
            We typically respond within 24 hours.
          </p>
          <button
            onClick={() => {
              setShowSuccessModal(false);
              setSelectedTier(null);
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
          >
            OK
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  const handlePricingClick = (tier) => {
    setSelectedTier(tier);
    document.getElementById('contactForm')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleFormSubmit = async (e) => {
      e.preventDefault();
      
      const formData = {
        name: e.target.name.value,
        email: e.target.email.value,
        message: e.target.message.value,
        package: selectedTier?.name || "Not specified"
      };
      
      try {
        const response = await fetch(
          'https://c08ecwt84i.execute-api.eu-north-1.amazonaws.com/contactFormHandler', 
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData)
          }
        );
        
        const result = await response.json();
        
        if (result.success) {
          setShowSuccessModal(true);
          e.target.reset();
        } else {
          alert('Error: ' + (result.error || 'Submission failed'));
        }
      } catch (error) {
        alert('Network error: ' + error.message);
      }
    };

    const form = document.getElementById('contactForm');
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
      return () => form.removeEventListener('submit', handleFormSubmit);
    }
  }, [selectedTier]);

  return (
    <>
      {/* Header with Animated Gradient */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white text-center py-12 shadow-xl relative overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <img
            src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png"
            alt="HunterFortress Logo"
            className="mx-auto h-24 mb-6 drop-shadow-lg"
          />
          <motion.h1 
            className="text-5xl font-extrabold tracking-tight mb-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            HunterFortress
          </motion.h1>
          <motion.p 
            className="text-xl font-light max-w-2xl mx-auto opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Fortifying your cloud infrastructure with military-grade security
          </motion.p>
        </motion.div>
        
        {/* Animated radar pulse effect */}
        <motion.div 
          className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full border-2 border-blue-300 opacity-20"
          animate={{ 
            scale: [1, 1.5, 2],
            opacity: [0.2, 0.1, 0]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 3,
            ease: "easeOut"
          }}
        />
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-16 space-y-20">
        {/* Trust Badges */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-lg text-gray-500 mb-6">TRUSTED BY SECURITY TEAMS AT</h3>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 items-center">
            {clientLogos.map((logo, index) => (
              <motion.img
                key={index}
                src={logo.src}
                alt={logo.alt}
                className="h-12 object-contain grayscale hover:grayscale-0 transition-all"
                whileHover={{ scale: 1.1 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              />
            ))}
          </div>
        </motion.section>

        {/* About Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-white p-8 rounded-xl shadow-lg border border-blue-100"
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center">
            <span className="bg-blue-100 p-2 rounded-full mr-3">🏰</span>
            About HunterFortress
          </h2>
          <div className="space-y-4 text-lg leading-relaxed text-gray-700">
            <p>
              We specialize in <span className="font-semibold text-blue-800">cloud security architecture</span> and <span className="font-semibold text-blue-800">DevSecOps automation</span> for startups and enterprises. 
            </p>
            <p>
              With 8+ years of battle-tested experience, we build <span className="font-semibold text-blue-800">impenetrable fortresses</span> in AWS, Azure, and GCP — combining cutting-edge tools with proven security frameworks.
            </p>
            <p>
              Our mission: Make robust security <span className="font-semibold text-blue-800">automatic, scalable, and invisible</span> to your development workflow.
            </p>
          </div>
        </motion.section>

        {/* Services Section */}
        <section>
          <motion.h2 
            className="text-3xl font-bold text-blue-900 mb-10 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <span className="border-b-4 border-blue-500 pb-2">Security Services</span>
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <motion.div 
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  className="text-4xl mb-4"
                >
                  {service.icon}
                </motion.div>
                <h3 className="text-xl font-bold text-blue-800 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {service.title.includes("AWS") && <span className="bg-gray-100 px-2 py-1 rounded text-xs">AWS</span>}
                  {service.title.includes("Azure") && <span className="bg-gray-100 px-2 py-1 rounded text-xs">Azure</span>}
                  {service.title.includes("Kubernetes") && <span className="bg-gray-100 px-2 py-1 rounded text-xs">K8s</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Case Studies */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-blue-50 p-8 rounded-xl"
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">
            <span className="border-b-4 border-blue-500 pb-2">Proven Results</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600"
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <h3 className="text-xl font-bold text-blue-800 mb-2">{study.title}</h3>
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-2xl font-bold bg-blue-100 text-blue-800 px-3 py-1 rounded">
                    {study.result}
                  </span>
                  <span className="text-lg text-gray-600">
                    {study.stats}
                  </span>
                </div>
                <p className="text-gray-700">{study.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
		
		{/* Testimonials Section */}
		<motion.section
		  initial={{ opacity: 0 }}
		  whileInView={{ opacity: 1 }}
		  transition={{ duration: 0.6 }}
		  viewport={{ once: true }}
		  className="bg-gray-50 p-8 rounded-xl"
		>
		  <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">
			<span className="border-b-4 border-blue-500 pb-2">Client Feedback</span>
		  </h2>
		  <div className="grid md:grid-cols-3 gap-6">
			{testimonials.map((testimonial, index) => (
			  <motion.div
				key={index}
				className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ delay: index * 0.2 }}
			  >
				<div className="text-4xl mb-4">{testimonial.avatar}</div>
				<p className="text-gray-700 italic mb-4">"{testimonial.feedback}"</p>
				<div className="font-medium">
				  <p className="text-blue-800">{testimonial.name}</p>
				  <p className="text-gray-600 text-sm">{testimonial.role}</p>
				</div>
			  </motion.div>
			))}
		  </div>
		</motion.section>

        {/* Certifications */}
        <motion.section 
          className="bg-blue-900 text-white p-8 rounded-xl"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold mb-8 flex items-center">
            <span className="bg-white text-blue-900 p-2 rounded-full mr-3">📜</span>
            Certifications
          </h2>
          <ul className="space-y-3 text-lg">
            {certifications.map((cert, index) => (
              <motion.li 
                key={index}
                className="flex items-start"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <span className="text-blue-300 mr-2">✔</span> {cert}
              </motion.li>
            ))}
          </ul>
        </motion.section>

      {/* Pricing */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-blue-900 mb-10">
          <span className="border-b-4 border-blue-500 pb-2">Security Packages</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={index}
              className={`bg-white p-6 rounded-xl shadow-lg relative overflow-hidden ${
                index === 1 ? "border-2 border-blue-600" : "border border-gray-200"
              }`}
              initial={{ 
                scale: index === 1 ? 1.05 : 0.95,
                y: index === 1 ? 0 : 10
              }}
              whileHover={{
                scale: 1.05,
                y: -5,
                zIndex: 10,
                transition: { duration: 0.2 }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
              animate={{
                scale: index === 1 ? 1.05 : 0.95,
                y: index === 1 ? 0 : 10,
                transition: { 
                  type: "spring", 
                  stiffness: 300,
                  damping: 15,
                  mass: 0.5
                }
              }}
              onClick={() => handlePricingClick(tier)}
            >
              {/* Highlight effect for middle card */}
              {index === 1 && (
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white opacity-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ duration: 0.5 }}
                />
              )}
              
              <h3 className="text-2xl font-bold text-blue-800 mb-2 relative z-10">
                {tier.name}
              </h3>
			  <div className="mb-2 relative z-10">
              <p className="text-4xl font-bold mb-2 relative z-10">{tier.price}</p>
			  <p className="text-lg text-gray-600">~ {tier.price_usd}</p>
			  </div>
              <p className="text-gray-600 mb-4 relative z-10">{tier.desc}</p>
              <ul className="space-y-2 text-left mb-6 relative z-10">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span> {feature}
                  </li>
                ))}
              </ul>
              <motion.button
                className={`w-full py-3 rounded-lg font-bold relative z-10 ${
                  index === 1 
                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                } transition-colors`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {tier.name === "Enterprise" ? "Contact Us" : "Get Started"}
              </motion.button>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Contact Form */}
      <motion.section 
        className="bg-white p-8 rounded-xl shadow-xl border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-bold text-blue-900 mb-6">
          <span className="border-b-4 border-blue-500 pb-2">
            {selectedTier ? `Request ${selectedTier.name} Package` : 'Request Security Audit'}
          </span>
        </h2>
        <form
          id="contactForm"
          className="space-y-5"
        >
          <div>
            <label htmlFor="name" className="block text-gray-700 font-medium mb-1">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Your Name"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              required
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-gray-700 font-medium mb-1">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              required
            />
          </div>
          
          <div>
            <label htmlFor="message" className="block text-gray-700 font-medium mb-1">Security Needs</label>
            <textarea
              id="message"
              name="message"
              rows="5"
              placeholder={
                selectedTier 
                  ? `I'm interested in the ${selectedTier.name} package. ${selectedTier.desc}\n\nAdditional details:`
                  : "Describe your cloud environment and security challenges..."
              }
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              required
            ></textarea>
          </div>
          
          {selectedTier && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="font-medium text-blue-800">Selected Package: {selectedTier.name}</p>
              <p className="text-blue-700">{selectedTier.price} - {selectedTier.desc}</p>
			  <p className="text-blue-700">{selectedTier.price_usd}</p>
              <button
                type="button"
                onClick={() => setSelectedTier(null)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Change package
              </button>
            </div>
          )}
          
          <motion.button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-700 to-blue-600 text-white px-6 py-4 rounded-lg font-bold text-lg shadow-md hover:shadow-lg transition"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {selectedTier ? `Get ${selectedTier.name} Package` : 'Secure My Infrastructure'} →
          </motion.button>
        </form>
      </motion.section>

      {/* Contact Info */}
        <motion.section 
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Contact the Fortress</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-8 text-lg flex-wrap">
            <Link to="/contact" className="flex items-center justify-center gap-2 text-blue-700 hover:text-blue-900 transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Phone & address
            </Link>
            <a href="mailto:security@hunterfortress.com" className="flex items-center justify-center gap-2 text-blue-700 hover:text-blue-900 transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              security@hunterfortress.com
            </a>
            <a href="https://github.com/My-Thoth" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-blue-700 hover:text-blue-900 transition">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"></path>
              </svg>
              GitHub
            </a>
          </div>
        </motion.section>
      </main>

      

      {/* Success Modal */}
      {showSuccessModal && <SuccessModal />}
    </>
  );
}
